import { CaretLeftOutlined, CaretRightOutlined } from '@ant-design/icons'
import {
  Button,
  Layout,
  Tooltip,
} from 'antd'
import classNames from 'classnames'
import React, {
  PropsWithChildren,
  ReactNode, useCallback, useState,
} from 'react'

import { selectCurrentMenu } from '@/redux/menu-selector'
import { useAppSelector } from '@/redux/utils'

import s from './page.module.css'

export interface PageProps extends PropsWithChildren {
  headerLeft?: ReactNode
  headerRight?: ReactNode
  subHeader?: ReactNode
  sider?: ReactNode
  hideHeader?: boolean
  siderCollapsed?: boolean;
  siderCollapsible?: boolean
  onSiderCollapsedChange?: (c: boolean) => void;
  siderWidth?: number
  siderClassName?: string
  contentClassName?: string
}

export default function Page(
  {
    headerLeft,
    headerRight,
    subHeader,
    sider,
    siderCollapsible = false,
    siderCollapsed = false,
    siderWidth,
    onSiderCollapsedChange,
    children,
    hideHeader,
    siderClassName,
    contentClassName,
  }: PageProps) {
  const { menu } = useAppSelector(selectCurrentMenu)
  const [collapsed, setCollapsed] = useState(siderCollapsed)

  const handleChangeCollapsed = useCallback(() => {
    const nextCollapsed = !collapsed
    setCollapsed(nextCollapsed)
    onSiderCollapsedChange?.(nextCollapsed)
  }, [collapsed, onSiderCollapsedChange])

  return (
    <Layout className={s.Page}>
      {!hideHeader && (
        <div className={s.Header}>
          {headerLeft || <div className={s.FeatureName}>{menu?.name}</div>}
          <div>
            {headerRight}
          </div>
        </div>
      )}
      {subHeader && <div className={s.SubHeader}>{subHeader}</div>}
      <Layout>
        {sider &&
          <Layout.Sider
            trigger={null}
            collapsed={collapsed}
            collapsible={siderCollapsible}
            collapsedWidth={0}
            width={siderWidth}
            className={classNames(s.Sider, siderClassName)}>
            {sider}
            {siderCollapsible &&
              <Tooltip
                placement="right"
                title={collapsed ? 'Show groups' : 'Hide groups'}>
                <Button
                  type="primary"
                  onClick={handleChangeCollapsed}
                  className={classNames(s.toggleButton, { [s.toggleButtonCollapsed]: collapsed })}
                  icon={collapsed ? <CaretRightOutlined /> : <CaretLeftOutlined />}
                />
              </Tooltip>
            }
          </Layout.Sider>}
        <Layout.Content className={classNames(s.Content, contentClassName)}>
          {children}
        </Layout.Content>
      </Layout>
    </Layout>
  )
}
